import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Shield, Pencil } from 'lucide-react';

export function EditQualification({
  isOpen,
  onClose,
  selectedQualification,
  onSuccess,
  teamMemberCount
}) {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 'medium',
    project_id: '',
    calculated_required: null,
    renewal_period: '',
    category: '',
    regulatory_body: '',
    regulation_reference: '',
    legal_requirement: false,
    notes: ''
  });

  // Track whether user wants number or percentage
  const [requirementType, setRequirementType] = useState('number');

  // Derive course type from the qualification
  const courseType = selectedQualification?.course_link_type || null;
  const isCustomCourse = courseType === 'custom';

  // Renewal periods
  const renewalPeriods = [
    { value: 'none', label: 'No Renewal Required' },
    { value: '6_months', label: 'Every 6 Months' },
    { value: '1_year', label: 'Every Year' },
    { value: '2_years', label: 'Every 2 Years' },
    { value: '3_years', label: 'Every 3 Years' },
    { value: '5_years', label: 'Every 5 Years' },
    { value: 'custom', label: 'Custom Period' }
  ];

  // Populate form when selectedQualification changes
  useEffect(() => {
    if (selectedQualification && isOpen) {
      // Determine if the stored value is a percentage or number by checking minimum_personnel
      const minimumPersonnel = selectedQualification.minimum_personnel;
      let isPercentage = false;
      let displayValue = selectedQualification.calculated_required;

      if (
        minimumPersonnel &&
        typeof minimumPersonnel === 'string' &&
        minimumPersonnel.includes('%')
      ) {
        isPercentage = true;
      }

      setRequirementType(isPercentage ? 'percentage' : 'number');
      setFormData({
        name: selectedQualification.name || '',
        description: selectedQualification.description || '',
        priority: selectedQualification.priority || 'medium',
        project_id: selectedQualification.project_id || '',
        calculated_required: displayValue,
        renewal_period: selectedQualification.renewal_period || '',
        category: selectedQualification.category || '',
        regulatory_body: selectedQualification.regulatory_body || '',
        regulation_reference: selectedQualification.regulation_reference || '',
        legal_requirement: selectedQualification.legal_requirement || false,
        notes: selectedQualification.notes || ''
      });
    }
  }, [selectedQualification, isOpen]);

  // Handle dialog close - reset form before closing
  const handleDialogClose = () => {
    // Reset all form data
    setFormData({
      name: '',
      description: '',
      priority: 'medium',
      project_id: '',
      calculated_required: null,
      renewal_period: '',
      category: '',
      regulatory_body: '',
      regulation_reference: '',
      legal_requirement: false,
      notes: ''
    });
    setRequirementType('number');

    // Call parent's onClose
    onClose();
  };

  // Handle dialog open/close state changes
  const handleOpenChange = open => {
    if (!open) {
      handleDialogClose();
    }
  };

  const handleSave = async () => {
    try {
      if (isCustomCourse && !formData.name.trim()) {
        toast.error('Qualification name is required');
        return;
      }

      // Store integer value in calculated_required and type in minimum_personnel
      const calculatedValue = formData.calculated_required;
      const minimumPersonnelValue =
        requirementType === 'percentage'
          ? `${formData.calculated_required}%`
          : String(formData.calculated_required || '');

      const updatePayload = {
        priority: formData.priority,
        calculated_required: calculatedValue,
        minimum_personnel: minimumPersonnelValue,
        renewal_period: formData.renewal_period || null,
        category: formData.category || null,
        regulatory_body: formData.regulatory_body || null,
        regulation_reference: formData.regulation_reference || null,
        legal_requirement: formData.legal_requirement,
        notes: formData.notes || null,
        updated_date: new Date().toISOString()
      };

      // Include name in update if custom course was renamed
      if (isCustomCourse) {
        updatePayload.name = formData.name.trim();
      }

      const { error } = await supabase
        .from('compliance_requirements')
        .update(updatePayload)
        .eq('id', selectedQualification.id);

      if (error) throw error;

      // Sync renamed title to custom_courses table
      if (isCustomCourse && formData.name.trim() !== selectedQualification.name) {
        const { error: customError } = await supabase
          .from('custom_courses')
          .update({
            title: formData.name.trim(),
            updated_date: new Date().toISOString()
          })
          .eq('id', parseInt(selectedQualification.course_link_id, 10));

        if (customError) {
          console.error('Error syncing custom course title:', customError);
          // Non-fatal: requirement was already updated
        }
      }

      toast.success('Qualification updated successfully');
      handleDialogClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error updating qualification:', error);
      toast.error('Failed to update qualification');
    }
  };

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>Edit Qualification</DialogTitle>
          <DialogDescription>
            Update requirements and settings for this qualification.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={e => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className='space-y-6 py-4'>
            {/* Course Information */}
            <div className='space-y-2 pb-6 border-b'>
              <div className='flex items-start justify-between gap-2'>
                <div className='flex-1 min-w-0'>
                  {isCustomCourse ? (
                    <div className='space-y-1.5'>
                      <Input
                        value={formData.name}
                        onChange={e =>
                          setFormData(prev => ({ ...prev, name: e.target.value }))
                        }
                        placeholder='Qualification name'
                        className='font-semibold text-sm'
                      />
                    </div>
                  ) : (
                    <p className='text-sm font-semibold text-gray-900'>
                      {formData.name || 'Not specified'}
                    </p>
                  )}
                </div>
                {courseType && (
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                    courseType === 'verified'
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}>
                    {courseType === 'verified' ? (
                      <Shield className='w-3 h-3' />
                    ) : (
                      <Pencil className='w-3 h-3' />
                    )}
                    {courseType === 'verified' ? 'Verified' : 'Custom'}
                  </span>
                )}
              </div>
              {formData.description && (
                <div>
                  <p className='text-sm text-muted-foreground'>
                    {formData.description}
                  </p>
                </div>
              )}
            </div>

            {/* Requirements Section */}
            <div className='space-y-2'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='priority'>Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={value =>
                      setFormData(prev => ({ ...prev, priority: value }))
                    }
                  >
                    <SelectTrigger className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='high'>High</SelectItem>
                      <SelectItem value='medium'>Medium</SelectItem>
                      <SelectItem value='low'>Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='category'>Category</Label>
                  <Select
                    value={formData.category || undefined}
                    onValueChange={value =>
                      setFormData(prev => ({ ...prev, category: value }))
                    }
                  >
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder='Select category' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='Equipment'>Equipment</SelectItem>
                      <SelectItem value='Safety'>Safety</SelectItem>
                      <SelectItem value='Compliance'>Compliance</SelectItem>
                      <SelectItem value='Training'>Training</SelectItem>
                      <SelectItem value='Certification'>Certification</SelectItem>
                      <SelectItem value='Environmental'>Environmental</SelectItem>
                      <SelectItem value='Quality'>Quality</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='renewal_period'>Renewal Period</Label>
                <Select
                  value={formData.renewal_period || undefined}
                  onValueChange={value =>
                    setFormData(prev => ({
                      ...prev,
                      renewal_period: value
                    }))
                  }
                >
                  <SelectTrigger className='w-full'>
                    <SelectValue placeholder='Select renewal period' />
                  </SelectTrigger>
                  <SelectContent>
                    {renewalPeriods.map(period => (
                      <SelectItem key={period.value} value={period.value}>
                        {period.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <Label className='text-sm font-medium text-gray-900'>
                  Required Personnel
                </Label>

                <div className='flex items-center gap-2'>
                  {/* Premium Toggle Selector */}
                  <div className='inline-flex items-center bg-gray-100 rounded-lg p-1 gap-1'>
                    <button
                      type='button'
                      onClick={() => setRequirementType('number')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        requirementType === 'number'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Specific Number
                    </button>
                    <button
                      type='button'
                      onClick={() => setRequirementType('percentage')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        requirementType === 'percentage'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Percentage
                    </button>
                  </div>

                  {/* Input field */}
                  <div className='relative flex-1'>
                    <Input
                      id='required'
                      type='number'
                      min='0'
                      max={requirementType === 'percentage' ? 100 : undefined}
                      value={formData.calculated_required || ''}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          calculated_required: e.target.value
                            ? parseInt(e.target.value)
                            : null
                        })
                      }
                      placeholder={
                        requirementType === 'percentage' ? 'e.g., 100' : 'e.g., 20'
                      }
                      className={requirementType === 'percentage' ? 'pr-10' : ''}
                    />
                    {requirementType === 'percentage' && (
                      <div className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded bg-gray-100 pointer-events-none'>
                        <span className='text-sm font-medium text-gray-600'>%</span>
                      </div>
                    )}
                  </div>
                </div>
                {requirementType === 'percentage' && teamMemberCount > 0 && formData.calculated_required > 0 && (
                  <p className='text-xs text-gray-500 mt-1'>
                    = <span className='font-medium text-gray-700'>{Math.ceil((formData.calculated_required / 100) * teamMemberCount)}</span> of {teamMemberCount} team members
                  </p>
                )}
              </div>
            </div>

            {/* Regulatory Information */}
            <div className='space-y-2'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='regulatory_body'>Regulatory Body</Label>
                  <Input
                    id='regulatory_body'
                    name='regulatory_body'
                    value={formData.regulatory_body}
                    onChange={handleInputChange}
                    placeholder='e.g., OSHA, HSE, CITB'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='regulation_reference'>
                    Regulation Reference
                  </Label>
                  <Input
                    id='regulation_reference'
                    name='regulation_reference'
                    value={formData.regulation_reference}
                    onChange={handleInputChange}
                    placeholder='e.g., 29 CFR 1910.151'
                  />
                </div>
              </div>

              <div className='flex items-start space-x-3 bg-red-50 border border-red-200 rounded-lg p-3'>
                <Checkbox
                  id='legal_requirement'
                  checked={formData.legal_requirement}
                  onCheckedChange={checked =>
                    setFormData(prev => ({
                      ...prev,
                      legal_requirement: checked
                    }))
                  }
                  className='mt-0.5'
                />
                <div className='flex-1'>
                  <Label
                    htmlFor='legal_requirement'
                    className='flex items-center gap-2 cursor-pointer font-medium text-sm text-red-900'
                  >
                    <Shield className='w-4 h-4 text-red-600' />
                    Legal/Regulatory Requirement
                  </Label>
                  <p className='text-xs text-red-700 mt-1'>
                    Check this if failing to meet this requirement could result
                    in legal consequences
                  </p>
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='notes'>Additional Notes</Label>
                <Textarea
                  id='notes'
                  name='notes'
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder='Add any additional information, exemptions, or special instructions...'
                  rows={3}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type='submit'>Update Qualification</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
